/**
 * The home's "Coming up" section — which items it shows, in which order, under which chip, and which
 * of its states the section is in. Spec: `artifacts/prototypes/home-v3/COMING-UP.md` (Oct 18 2026, and
 * the "Show all" modal added Oct 19).
 *
 * Shared 1:1 with iOS `ComingUp.swift` and Android `ComingUp.kt`; all three unit-test the same table.
 * Pure, with imports only from other import-free modules, so the test does not pull the app's graph.
 *
 * ## Where the items come from
 *
 * There is no "coming up" endpoint. The section is built on the client from five reads every surface
 * already makes for the home:
 *
 * - **meetings and bookings** — the Calendar projection's dated external occurrences.
 * - **tasks** — a task the agent will start at a set time (`isScheduledTask`: PENDING with a future
 *   `notBefore`) is an upcoming TASK. Undated owner-blocked work stays in Action needed.
 * - **routines** — a routine that is on and has a `nextRun` is an upcoming ROUTINE, one card per routine
 *   (its next run only). A paused one, and a kit offer nobody switched on, never shows.
 * - **goals** — an open goal with an end date (`endsAt`) is a DEADLINE: upcoming while the date is ahead,
 *   OVERDUE once it has passed and the goal is still not done.
 *
 * Every real card is backed by a recorded date. The section always draws three slots: when fewer than
 * three items exist, `comingUpSlots` fills the rest with suggested goals (spec "Fewer than 3 items").
 *
 * ## Slot 1 is the calendar, slots 2 and 3 are Symphony's own
 *
 * When the account has a connected calendar, slot 1 is always its next timed event (today's, else the
 * first one later in the window) and its button opens the Calendar. All-day events never take it.
 * Slots 2 and 3 are only tasks, routines and goal deadlines, so a busy calendar cannot push them out.
 * With no connected calendar there is no calendar slot, and all three are Symphony's.
 */
import type { BucketableTask } from './agent-task-buckets';
import { startOfDay } from './calendar/calendar-dates';
import { isScheduledTask } from './task-schedule';
import { routineIsOn, routineIsRecommended, type StatusableRoutine } from './routine-status';

/** The look-ahead window: the next 7 days. */
export const COMING_UP_WINDOW_MS = 7 * 86400 * 1000;
/** How many dated cards Home shows. */
export const COMING_UP_CARDS = 3;
/** At most this many overdue deadlines, anywhere in the section. The rest stay in their Space. */
export const COMING_UP_OVERDUE_CAP = 2;

export type ComingUpKind = 'meeting' | 'booking' | 'task' | 'routine' | 'deadline';

/** Stable source reference. Home opens every item at its recorded day in the account Calendar. */
export type ComingUpTarget =
  | { type: 'task'; id: string }
  | { type: 'routine'; id: string }
  | { type: 'goal'; id: string; workspaceId?: string }
  | { type: 'calendar'; day: string };

export interface ComingUpItem {
  /** Stable across reads: `<kind>:<source id>`. */
  id: string;
  kind: ComingUpKind;
  title: string;
  /** When it happens (task / routine run) or is due (deadline), ms. Absent on Needs you. */
  at?: number;
  /** When the source item was created, ms — the tie-break between two items at the same time. */
  createdAt?: number;
  /** The owning agent's id. Absent with `maestro` false means the item has no agent (a deadline). */
  agentId?: string;
  /** No agent named on it, but it is Maestro's own work. */
  maestro: boolean;
  workspaceId?: string;
  /** Second line when the item has no Space: the calendar a meeting is on. */
  sourceLabel?: string;
  /** An all-day calendar event: its chip names the day, never a clock time. */
  allDay?: boolean;
  target: ComingUpTarget;
}

/** One of the three slots: a real item, or a suggested goal (a goal-intent id) filling an empty one. */
export type ComingUpSlot = { type: 'item'; item: ComingUpItem } | { type: 'goal'; goalId: string };

/** The chip on a card or a modal row. Clock text is formatted by the caller in the reader's own locale. */
export type ComingUpChip =
  | { kind: 'overdue' }
  | { kind: 'allDay' }
  | { kind: 'tomorrowAllDay' }
  | { kind: 'today'; at: number }
  | { kind: 'tomorrow'; at: number }
  | { kind: 'inDays'; days: number }
  | { kind: 'routine'; at: number };

// ── Inputs ───────────────────────────────────────────────────────────────────────────────────────────

export type ComingUpTaskInput = BucketableTask & {
  id: string;
  title?: string;
  workspaceId?: string;
  agentId?: string;
  createdDate?: string | Date;
};

export type ComingUpRoutineInput = StatusableRoutine & {
  id: string;
  name?: string;
  workspaceId?: string;
  agentId?: string | null;
  nextRun?: string | null;
  createdDate?: string;
};

export interface ComingUpGoalInput {
  id: string;
  title: string;
  status: string;
  outcome?: string;
  workspaceId?: string;
  endsAt?: string;
  parentGoalId?: string;
  createdDate?: string;
}

export interface ComingUpInputs {
  tasks: readonly ComingUpTaskInput[];
  routines: readonly ComingUpRoutineInput[];
  goals: readonly ComingUpGoalInput[];
  calendarItems?: readonly {
    key: string;
    kind: 'meeting' | 'booking';
    title: string;
    start: number;
    end: number;
    day: string;
    sourceLabel?: string;
    allDay?: boolean;
  }[];
  /** The account has a connected calendar. Without one there is no calendar slot, whatever items are passed. */
  calendarConnected?: boolean;
  /** The ids of the Spaces the account holds, or null while unknown. An item in another Space (archived
   *  or deleted) is hidden; an item with no Space is kept. */
  liveWorkspaceIds: ReadonlySet<string> | null;
  now: number;
}

function ms(value: string | Date | null | undefined): number | undefined {
  if (!value) return undefined;
  const at = new Date(value).getTime();
  return Number.isNaN(at) ? undefined : at;
}

/** A goal is done once it reached ACHIEVED or carries any outcome (it settled, one way or another). */
function goalIsOpen(goal: ComingUpGoalInput): boolean {
  if (goal.outcome) return false;
  return goal.status !== 'ACHIEVED' && goal.status !== 'ABANDONED' && goal.status !== 'CANCELLED';
}

function inLiveSpace(workspaceId: string | undefined, live: ReadonlySet<string> | null): boolean {
  if (!workspaceId || !live) return true;
  return live.has(workspaceId);
}

/** Every candidate item, unsorted and uncapped. Exported for the tests. */
export function comingUpCandidates(input: ComingUpInputs): ComingUpItem[] {
  const { now, liveWorkspaceIds } = input;
  const horizon = now + COMING_UP_WINDOW_MS;
  const items: ComingUpItem[] = [];
  const seen = new Set<string>();
  const push = (item: ComingUpItem) => {
    if (seen.has(item.id) || !inLiveSpace(item.workspaceId, liveWorkspaceIds)) return;
    seen.add(item.id);
    items.push(item);
  };

  for (const event of input.calendarItems ?? []) {
    if (!event.key || !Number.isFinite(event.start) || !Number.isFinite(event.end) || event.end <= now || event.start > horizon) continue;
    push({
      id: event.key,
      kind: event.kind,
      title: event.title,
      at: event.start,
      maestro: false,
      sourceLabel: event.sourceLabel,
      allDay: event.allDay,
      target: { type: 'calendar', day: event.day },
    });
  }

  for (const task of input.tasks) {
    if (!task.id) continue;
    const agentId = (task.agentId ?? '').trim() || undefined;
    const base = {
      title: task.title ?? '',
      createdAt: ms(task.createdDate),
      agentId,
      maestro: !agentId,
      workspaceId: task.workspaceId || undefined,
      target: { type: 'task', id: task.id } as ComingUpTarget,
    };
    if (isScheduledTask(task, now)) {
      const at = ms(task.notBefore);
      if (at !== undefined && at <= horizon) push({ ...base, id: `task:${task.id}`, kind: 'task', at });
    }
  }

  for (const routine of input.routines) {
    if (!routine.id || !routineIsOn(routine) || routineIsRecommended(routine)) continue;
    const at = ms(routine.nextRun);
    if (at === undefined || at < now || at > horizon) continue;
    const agentId = (routine.agentId ?? '').trim() || undefined;
    push({
      id: `routine:${routine.id}`,
      kind: 'routine',
      title: routine.name ?? '',
      at,
      createdAt: ms(routine.createdDate),
      agentId,
      maestro: !agentId,
      workspaceId: routine.workspaceId || undefined,
      target: { type: 'routine', id: routine.id },
    });
  }

  for (const goal of input.goals) {
    if (!goal.id || !goalIsOpen(goal)) continue;
    const at = ms(goal.endsAt);
    if (at === undefined || at > horizon) continue;
    // A deadline names no agent: the Space alone is its second line.
    push({
      id: `goal:${goal.id}`,
      kind: 'deadline',
      title: goal.title,
      at,
      createdAt: ms(goal.createdDate),
      maestro: false,
      workspaceId: goal.workspaceId || undefined,
      target: { type: 'goal', id: goal.id, workspaceId: goal.workspaceId },
    });
  }

  return items;
}

/** Overdue = a deadline whose date has passed and that is still open. */
export function isOverdue(item: ComingUpItem, now: number): boolean {
  return item.kind === 'deadline' && item.at !== undefined && item.at < now;
}

const byCreated = (a: ComingUpItem, b: ComingUpItem) => (a.createdAt ?? Infinity) - (b.createdAt ?? Infinity);
const byTimeThenCreated = (a: ComingUpItem, b: ComingUpItem) => (a.at ?? 0) - (b.at ?? 0) || byCreated(a, b);

export interface ComingUpLists {
  /** What the three cards show (real items only, at most three): the calendar's next event first, if any. */
  cards: ComingUpItem[];
  /** What the "Show all" modal lists: the same first card, then every other item by time, overdue still capped. */
  all: ComingUpItem[];
}

export function isCalendarItem(item: ComingUpItem): boolean {
  return item.kind === 'meeting' || item.kind === 'booking';
}

/**
 * The calendar's next event: the earliest one with a clock time that has not ended. An all-day event is
 * background, not "next", so it never qualifies. The window already limits it to 7 days, so with nothing
 * left today this is the first event on a later day.
 */
export function nextCalendarItem(candidates: readonly ComingUpItem[]): ComingUpItem | undefined {
  return candidates.filter((i) => isCalendarItem(i) && !i.allDay).sort(byTimeThenCreated)[0];
}

/**
 * Slot 1 is the next calendar event (only with a connected calendar). The other slots are Symphony's own:
 * overdue deadlines first, then upcoming soonest first; a tie goes to the one created first. Overdue is
 * capped at two everywhere. Calendar events other than slot 1 appear only in `all`.
 */
export function buildComingUp(input: ComingUpInputs): ComingUpLists {
  const candidates = comingUpCandidates(input);
  const calendarItems = input.calendarConnected ? candidates.filter(isCalendarItem) : [];
  const next = nextCalendarItem(calendarItems);
  const own = candidates.filter((i) => !isCalendarItem(i));

  const overdue = own
    .filter((i) => isOverdue(i, input.now))
    .sort(byTimeThenCreated)
    .slice(0, COMING_UP_OVERDUE_CAP);
  const upcoming = own.filter((i) => !isOverdue(i, input.now)).sort(byTimeThenCreated);
  const ownSlots = COMING_UP_CARDS - (next ? 1 : 0);

  return {
    cards: [...(next ? [next] : []), ...[...overdue, ...upcoming].slice(0, ownSlots)],
    all: [
      ...(next ? [next] : []),
      ...overdue,
      ...[...upcoming, ...calendarItems.filter((i) => i !== next)].sort(byTimeThenCreated),
    ],
  };
}

/** The button on a card or a modal row: it names what it opens. */
export type ComingUpAction = 'showCalendar' | 'showTask' | 'showRoutine' | 'showGoal';

export function comingUpAction(item: ComingUpItem): ComingUpAction {
  switch (item.target.type) {
    case 'calendar':
      return 'showCalendar';
    case 'task':
      return 'showTask';
    case 'routine':
      return 'showRoutine';
    case 'goal':
      return 'showGoal';
  }
}

/**
 * Always three slots: real items first, then suggested goals for the empty ones. A goal the user already
 * started is left out of `goalIds` by the caller. When goals run out the slots stay empty (no filler card).
 */
export function comingUpSlots(cards: readonly ComingUpItem[], goalIds: readonly string[]): ComingUpSlot[] {
  const slots: ComingUpSlot[] = cards.slice(0, COMING_UP_CARDS).map((item) => ({ type: 'item', item }));
  for (const goalId of goalIds) {
    if (slots.length >= COMING_UP_CARDS) break;
    slots.push({ type: 'goal', goalId });
  }
  return slots;
}

// ── Chips ────────────────────────────────────────────────────────────────────────────────────────────

/** Whole calendar days from `now` to `at`, in `timeZone` when given, else in the device's. */
export function calendarDaysBetween(now: number, at: number, timeZone?: string): number {
  if (timeZone) return Math.round((startOfDay(at, timeZone) - startOfDay(now, timeZone)) / 86_400_000);
  const a = new Date(now);
  const b = new Date(at);
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((end - start) / 86400000);
}

export function comingUpChip(item: ComingUpItem, now: number, timeZone?: string): ComingUpChip {
  if (isOverdue(item, now)) return { kind: 'overdue' };
  const at = item.at ?? now;
  if (item.kind === 'routine') return { kind: 'routine', at };
  const days = calendarDaysBetween(now, at, timeZone);
  if (item.allDay && days <= 0) return { kind: 'allDay' };
  if (item.allDay && days === 1) return { kind: 'tomorrowAllDay' };
  if (days <= 0) return { kind: 'today', at };
  if (days === 1) return { kind: 'tomorrow', at };
  return { kind: 'inDays', days };
}

// ── States ───────────────────────────────────────────────────────────────────────────────────────────

export type ComingUpRead = 'loading' | 'ready' | 'error';

/**
 * - `loading` (E) — the first load: three placeholder cards, never "All clear".
 * - `failed` (G) — every source failed: one error block with Retry. Never `allClear` here.
 * - `items` — at least one dated item.
 * - `pickGoal` (C) — no dated item, but goals are left to suggest: "Pick a goal to get started" and three goal cards.
 * - `allClear` (D) — no dated item and no goal left to suggest.
 *
 * `partial` (F) rides beside `items`/`allClear`: a source failed while another answered, so
 * the section shows what it has and a "Some items could not load. Retry" line.
 */
export type ComingUpState = 'loading' | 'failed' | 'items' | 'pickGoal' | 'allClear';

export function comingUpState(
  reads: readonly ComingUpRead[],
  realItems: number,
  goalsLeft = 0,
): { state: ComingUpState; partial: boolean } {
  const partial = reads.some((r) => r === 'error');
  if (realItems > 0) return { state: 'items', partial };
  if (reads.length > 0 && reads.every((r) => r === 'error')) return { state: 'failed', partial: false };
  if (reads.some((r) => r === 'loading')) return { state: 'loading', partial: false };
  return { state: goalsLeft > 0 ? 'pickGoal' : 'allClear', partial };
}

/** "Show all" shows only when the section shows real items (state A or B). */
export function showsShowAll(state: ComingUpState): boolean {
  return state === 'items';
}
